import { forwardRef, useRef, type ChangeEvent } from 'react';
import { CalendarDays, Clock } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { MaskedInput, type MaskedInputProps } from '@/shared/components/MaskedInput';
import { dateBrToIso, isoToDateBr } from '@/shared/helpers/validators';
import styles from './DatePicker.module.scss';

export type DatePickerProps = Omit<MaskedInputProps, 'mask' | 'inputMode'>;

/**
 * Abre o seletor nativo ancorado num campo invisivel.
 *
 * `showPicker()` e o unico jeito de abrir o calendario (ou o relogio) do
 * navegador por codigo, e ele exige um campo **de fato renderizado** — por isso
 * o companheiro nativo fica com 1px e transparente, colado na base do campo
 * visivel, em vez de `display: none`. Onde o navegador nao tem `showPicker()`,
 * ou recusa a chamada, focar o campo ao menos abre o controle no toque.
 */
function abrirSeletorNativo(campo: HTMLInputElement | null): void {
  if (!campo) return;
  try {
    campo.showPicker();
  } catch {
    campo.focus();
  }
}

/**
 * Entrada de data no formato brasileiro `dd/mm/aaaa`, com calendario opcional.
 *
 * O campo em si e de texto, e **nao** um `<input type="date">`: o controle
 * nativo exibe o formato do sistema operacional, que num consultorio pode estar
 * em `mm/dd/aaaa`, e a data de nascimento e um dos campos mais digitados do
 * produto — quem sabe a data digita mais rapido do que navega um calendario.
 *
 * Quem prefere o calendario clica no icone: ele abre o seletor do navegador,
 * que devolve a data ja no formato do campo. Os dois caminhos escrevem no mesmo
 * valor, e o valor sobe mascarado — `dateBrToIso()` converte antes da API.
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  { value, onChange, disabled, ...rest },
  ref,
) {
  const nativo = useRef<HTMLInputElement>(null);

  const escolherNoCalendario = (evento: ChangeEvent<HTMLInputElement>): void => {
    // Campo limpo no calendário não apaga o que foi digitado: só substitui
    // quando há data escolhida.
    if (evento.target.value) onChange(isoToDateBr(evento.target.value));
  };

  return (
    <span className={styles.raiz}>
      <MaskedInput
        ref={ref}
        mask="date"
        placeholder="dd/mm/aaaa"
        maxLength={10}
        value={value}
        onChange={onChange}
        disabled={disabled}
        suffix={
          <button
            type="button"
            className={styles.gatilho}
            onClick={() => abrirSeletorNativo(nativo.current)}
            disabled={disabled}
            // Fora da ordem de tabulação: quem navega por teclado digita a data
            // no próprio campo, e um botão a mais entre um campo e o seguinte é
            // um obstáculo, não um atalho.
            tabIndex={-1}
            aria-label="Escolher no calendário"
          >
            <Icon as={CalendarDays} size={16} />
          </button>
        }
        {...rest}
      />

      <input
        ref={nativo}
        type="date"
        className={styles.nativo}
        value={dateBrToIso(value) ?? ''}
        onChange={escolherNoCalendario}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      />
    </span>
  );
});

export type TimePickerProps = Omit<MaskedInputProps, 'mask' | 'inputMode'>;

/**
 * Entrada de hora `hh:mm`, com relogio opcional.
 *
 * Mesmo desenho do `DatePicker`: digitar continua sendo o caminho mais rapido —
 * quatro teclas para "14:30" — e o icone abre o relogio do navegador para quem
 * prefere escolher. Diferente da data, a hora nativa ja e `hh:mm` em qualquer
 * sistema, entao o valor passa direto.
 */
export const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(function TimePicker(
  { value, onChange, disabled, ...rest },
  ref,
) {
  const nativo = useRef<HTMLInputElement>(null);

  return (
    <span className={styles.raiz}>
      <MaskedInput
        ref={ref}
        mask="time"
        placeholder="hh:mm"
        maxLength={5}
        value={value}
        onChange={onChange}
        disabled={disabled}
        suffix={
          <button
            type="button"
            className={styles.gatilho}
            onClick={() => abrirSeletorNativo(nativo.current)}
            disabled={disabled}
            tabIndex={-1}
            aria-label="Escolher a hora"
          >
            <Icon as={Clock} size={16} />
          </button>
        }
        {...rest}
      />

      <input
        ref={nativo}
        type="time"
        className={styles.nativo}
        value={value}
        onChange={(evento) => {
          if (evento.target.value) onChange(evento.target.value);
        }}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      />
    </span>
  );
});
